/**
 * AppShell：统一页面框架（LeapWorldUI-03，D29 下沉布局入包；布局形态遵守 T139 裁决）。
 * 无业务语义：导航经 nav 注入（分组/图标/激活态/徽标数），用户区是 slot，品牌区是 slot。
 * 形态：桌面左侧栏 w-56（用户区在侧栏底部）+ 仅移动端细顶栏 + w-64 抽屉；
 * 抽屉复用 use-overlay 焦点陷阱（ESC / 遮罩 / 焦点圈 / 焦点还原 / 滚动锁定）。
 *
 * 消费约定：应用 `src/features/shell/AppLayout.tsx` 只做薄壳——把 nav.ts 映射成
 * ShellNavGroup[] 并装配品牌/用户区。路由库无关：`href` + `onSelect` 同时给时
 * 点击先 preventDefault 再调 onSelect（react-router 消费方在 onSelect 里 navigate，
 * 得到 SPA 跳转）；只给 href 是整页跳转（静态手册等站外链接）；只给 onSelect 渲染为
 * 状态式导航（crm/foodbuy 形态）。
 *
 * `chrome`（LeapWorldTwin-111，11-ui-kit §10「导航方位」修订②）：默认按当前系列决定——
 * TechBlue 桌面用 `topbar-sidebar`（56px 品牌顶栏 + 侧栏，侧栏品牌区隐藏），其余系列仍是
 * 纯侧栏（`sidebar`）。系列信息在运行时只体现在 `html[data-theme]`（构建期注入），
 * 组件自己读这个属性判默认值，消费方不需要另外告诉 AppShell 当前系列是什么。
 * 不在顶栏重复用户身份——顶栏只放品牌与 `topBarEnd`，用户区仍在侧栏底部（评审 §1 批评）。
 */
import { useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react'
import { Avatar } from './Avatar'
import { Button } from './Button'
import { Menu, X, type IconComponent } from './icons'
import { overlayBackdropTransition, overlayPanelTransition, useEnterTransition, useOverlay } from './use-overlay'

export type AppShellChrome = 'sidebar' | 'topbar-sidebar'

export type ShellNavItem = {
  /** 稳定唯一键（通常取 href 或视图 id） */
  key: string
  label: string
  /** @bf/ui 内嵌图标（icons 子集），可选 */
  icon?: IconComponent
  /** 徽标数；>99 显示 99+，0/缺省不显示 */
  badge?: number
  /** 激活态由消费方按当前路由/视图计算 */
  active?: boolean
  href?: string
  onSelect?: () => void
}

export type ShellNavGroup = {
  title: string
  items: ShellNavItem[]
}

export type ShellUser = {
  name: string
  /** 第二行弱化信息：角色/部门等 */
  detail?: string
}

export type AppShellProps = {
  /** 桌面侧栏与抽屉顶部的品牌区（名称 + 副标题块） */
  brand: ReactNode
  /** 移动细顶栏品牌，缺省复用 brand */
  mobileBrand?: ReactNode
  nav: ShellNavGroup[]
  /** 用户区身份；不给则不渲染用户区 */
  user?: ShellUser
  /**
   * 自定义完整用户区。与 user / userActions 互斥：给了 userPanel 就不再渲染默认身份行，
   * userPanel 优先。面板下方仍追加 versionBadge('sidebar')。
   */
  userPanel?: ReactNode
  /** 用户区右侧操作位（帮助/退出等，样式归应用） */
  userActions?: ReactNode
  /** 移动细顶栏右侧槽位（如 Avatar） */
  topBarEnd?: ReactNode
  /**
   * 版本徽章槽位（LeapWorldFlow-14）：函数形式而不是直接给 ReactNode——同一个组件在
   * 两处的弹层展开方向必须不同（评审 R2：侧栏/抽屉用户区在视口底部，弹层若像顶栏那样
   * 向下展开会被推出视口，1280×800 实测 68% 出屏），所以 AppShell 按渲染位置各调一次，
   * 把 `'topbar' | 'sidebar'` 传给消费方决定怎么摆。同时渲染在移动细顶栏（与 topBarEnd
   * 并排，向下展开）与桌面侧栏/抽屉用户区下方**新起一行**（向上展开，不挤用户区那一行的
   * 姓名列，评审对「userActions 单行会把姓名挤没」T69 §2.7 的顾虑只对单行成立）。
   */
  versionBadge?: (align: 'topbar' | 'sidebar') => ReactNode
  /** 内容容器：default = 90rem 居中；none = 不套容器（应用自管页宽） */
  contentWidth?: 'default' | 'none'
  /** 外壳形态：不给则按当前系列（`html[data-theme]`）默认——techblue 是 topbar-sidebar，其余 sidebar */
  chrome?: AppShellChrome
  children: ReactNode
}

/** 默认按系列决定形态：techblue 是 topbar-sidebar，其余（lark/linear，含未识别/SSR 场景）是 sidebar。 */
function defaultChrome(): AppShellChrome {
  if (typeof document === 'undefined') return 'sidebar'
  return document.documentElement.dataset.theme === 'techblue' ? 'topbar-sidebar' : 'sidebar'
}

/**
 * 导航图标一致性（11-ui-kit「导航图标登记制」）：同一分组要么全部登记图标，要么全部不登记——
 * 半登记比「全组无图标」观感更差。开发态提示，不阻断渲染（缺图标不是渲染错误，只是形态不一致）。
 */
function warnIfNavIconsInconsistent(nav: ShellNavGroup[]): void {
  for (const group of nav) {
    const withIcon = group.items.filter((item) => item.icon).length
    if (withIcon > 0 && withIcon < group.items.length) {
      console.warn(
        `[AppShell] 导航分组「${group.title}」图标半登记（${withIcon}/${group.items.length} 项有图标）：11-ui-kit「导航图标登记制」要求同组要么全有要么全无。`
      )
    }
  }
}

function ShellNavLink({ item, onNavigate }: { item: ShellNavItem; onNavigate?: () => void }) {
  // 焦点态与 §9.3 全控件基线对齐：导航项此前只有浏览器默认 outline，与其它控件的统一焦点环不一致
  const className = `relative flex min-h-[var(--nav-height)] items-center justify-between gap-2 rounded-[var(--radius-md)] px-3 py-1.5 text-[length:var(--text-body)] no-underline motion-safe:transition-colors focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus)] max-md:min-h-11 ${
    item.active
      ? 'bg-[var(--color-nav-on-bg)] font-medium text-[var(--color-nav-on-ink)] shadow-[var(--shadow-nav-on)] before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-[var(--color-nav-on-bar)]'
      : 'text-[var(--color-muted)] hover:bg-[var(--color-soft)] hover:text-[var(--color-ink)]'
  }`
  const content = (
    <>
      <span className="flex min-w-0 items-center gap-2.5">
        {item.icon ? <item.icon size={16} className="shrink-0" /> : null}
        <span className="truncate">{item.label}</span>
      </span>
      {item.badge ? (
        <span className="shrink-0 rounded-[var(--radius-pill)] bg-[var(--color-danger)] px-1.5 py-px text-[11px] font-medium leading-4 text-[var(--color-on-primary)]">
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      ) : null}
    </>
  )
  const onClick = (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (item.onSelect) {
      event.preventDefault()
      item.onSelect()
    }
    onNavigate?.()
  }
  if (item.href) {
    return (
      <a href={item.href} aria-current={item.active ? 'page' : undefined} className={className} onClick={onClick}>
        {content}
      </a>
    )
  }
  return (
    <button type="button" aria-current={item.active ? 'page' : undefined} className={`cursor-pointer ${className}`} onClick={onClick}>
      {content}
    </button>
  )
}

export function AppShell({ brand, mobileBrand, nav, user, userPanel, userActions, topBarEnd, versionBadge, contentWidth = 'default', chrome, children }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const closeDrawer = () => setDrawerOpen(false)
  const panelRef = useOverlay<HTMLElement>(drawerOpen, closeDrawer)
  const drawerEntered = useEnterTransition(drawerOpen)
  const resolvedChrome = chrome ?? defaultChrome()
  const isTopbarSidebar = resolvedChrome === 'topbar-sidebar'
  warnIfNavIconsInconsistent(nav)

  // Brand slots already consume ink/faint (including runtime aliases); scope both to the brand surface.
  const brandColors = {
    '--bf-color-ink': 'var(--bf-color-brand-ink)',
    '--color-ink': 'var(--bf-color-brand-ink)',
    '--bf-color-faint': 'var(--bf-color-brand-secondary)',
    '--color-faint': 'var(--bf-color-brand-secondary)',
    '--bf-color-muted': 'var(--bf-color-brand-secondary)',
    '--color-muted': 'var(--bf-color-brand-secondary)'
  } as CSSProperties

  const navBlock = (onNavigate?: () => void) => (
    <nav aria-label="主导航" className="flex flex-col gap-4 px-3 py-4">
      {nav.map((group) => (
        <div key={group.title}>
          <p className="m-0 px-3 pb-1 text-xs font-medium tracking-[0.06em] text-[var(--color-ink-secondary)]">{group.title}</p>
          <div className="flex flex-col gap-0.5">
            {group.items.map((item) => (
              <ShellNavLink key={item.key} item={item} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      ))}
    </nav>
  )

  const userBlock = userPanel ? (
    <div className="border-t border-[var(--color-line-soft)] px-3 py-4">
      {userPanel}
      {versionBadge ? <div className="mt-2 flex items-center">{versionBadge('sidebar')}</div> : null}
    </div>
  ) : user ? (
    <div className="border-t border-[var(--color-line-soft)] px-4 py-4">
      <div className="flex items-center gap-2.5">
        <Avatar name={user.name} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-[var(--color-ink)]">{user.name}</span>
          {user.detail ? <span className="block truncate text-[length:var(--text-caption)] text-[var(--color-ink-secondary)]">{user.detail}</span> : null}
        </span>
        {userActions}
      </div>
      {/* 版本徽章另起一行（Flow-14 评审 M1）：不挤上面这行的姓名/操作位；
          'sidebar' 对齐（评审 R2）：这里在视口底部，弹层要向上展开 */}
      {versionBadge ? <div className="mt-2 flex items-center">{versionBadge('sidebar')}</div> : null}
    </div>
  ) : null

  return (
    <div className="flex min-h-screen flex-col bg-[var(--color-bg)] text-[length:var(--text-body)]">
      {/* 桌面 56px 品牌顶栏（chrome="topbar-sidebar"，评审 §1：不重复用户身份，只放品牌 + topBarEnd） */}
      {isTopbarSidebar ? (
        <header
          style={brandColors}
          className="sticky top-0 z-40 hidden h-14 shrink-0 items-center justify-between gap-2 border-b-[length:var(--brand-rule-width)] border-[var(--color-brand-line)] bg-[var(--color-brand-bg)] px-4 text-[var(--color-brand-ink)] md:flex"
        >
          <span className="min-w-0 flex-1 truncate text-sm font-bold">{brand}</span>
          {topBarEnd}
        </header>
      ) : null}

      {/* 移动细顶栏（T139：仅移动端，品牌 + 菜单；两种 chrome 形态一致，不受 topbar-sidebar 影响） */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-2 border-b border-[var(--color-line)] bg-[var(--color-paper)] px-3 md:hidden">
        <Button
          variant="icon"
          aria-label="打开菜单"
          aria-expanded={drawerOpen}
          aria-controls="bf-shell-drawer"
          onClick={() => setDrawerOpen(true)}
        >
          <Menu size={20} />
        </Button>
        <span className="min-w-0 flex-1 truncate text-sm font-bold text-[var(--color-ink)]">{mobileBrand ?? brand}</span>
        {versionBadge?.('topbar')}
        {topBarEnd}
      </header>

      {/* 移动抽屉（w-64，复用 use-overlay 焦点陷阱；进场过渡与遮罩 onMouseDown 同 Drawer 惯例） */}
      {drawerOpen ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className={`absolute inset-0 bg-[var(--color-overlay)] ${overlayBackdropTransition} ${drawerEntered ? 'opacity-100' : 'opacity-0'}`}
            aria-hidden="true"
            onMouseDown={closeDrawer}
          />
          <section
            ref={panelRef}
            id="bf-shell-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="导航菜单"
            className={`absolute inset-y-0 left-0 flex w-64 flex-col bg-[var(--color-surface-sunken)] shadow-[var(--shadow-lg)] ${overlayPanelTransition} ${drawerEntered ? '' : '-translate-x-4 opacity-0'}`}
          >
            <div style={brandColors} className="flex min-h-16 shrink-0 items-center justify-between gap-2 border-b-[length:var(--brand-rule-width)] border-[var(--color-brand-line)] bg-[var(--color-brand-bg)] px-4 py-3 text-[var(--color-brand-ink)]">
              <span className="min-w-0 flex-1 truncate text-sm font-bold text-[var(--color-ink)]">{mobileBrand ?? brand}</span>
              <Button variant="icon" aria-label="关闭菜单" onClick={closeDrawer}>
                <X size={18} />
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">{navBlock(closeDrawer)}</div>
            {userBlock}
          </section>
        </div>
      ) : null}

      <div className="flex flex-1">
        {/* 桌面侧栏（w-56，用户区在底部）。topbar-sidebar 形态下品牌区隐藏（顶栏已经有）、
            sticky 偏移量让到顶栏高度下方，两者才不会互相覆盖。 */}
        <aside
          className={`sticky hidden w-56 shrink-0 flex-col border-r border-[var(--color-line)] bg-[var(--color-surface-sunken)] md:flex ${
            isTopbarSidebar ? 'top-14 h-[calc(100vh-3.5rem)]' : 'top-0 h-screen'
          }`}
        >
          {!isTopbarSidebar ? (
            <div style={brandColors} className="flex min-h-16 shrink-0 items-center border-b-[length:var(--brand-rule-width)] border-[var(--color-brand-line)] bg-[var(--color-brand-bg)] px-4 py-3 text-[var(--color-brand-ink)]">{brand}</div>
          ) : null}
          <div className="min-h-0 flex-1 overflow-y-auto">{navBlock()}</div>
          {userBlock}
        </aside>

        <main className="min-w-0 flex-1 pt-14 md:pt-0">
          {contentWidth === 'none' ? children : <div className="mx-auto max-w-[90rem] px-4 py-6 sm:px-6">{children}</div>}
        </main>
      </div>
    </div>
  )
}
